import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser, useWebSocket } from './WebSocketContext';
import { createNavigationStorage } from '../Utils/navigationMemory';
import AddProject from './AddProject';
import { alpha } from '@mui/material/styles';
import dataunfoldMark from '../images/logo-7-80x80.png';
import OrganizationLink from './OrganizationLink';

const SaasWelcome = ({ data, busy, error, canCreate, trialBlocked, expired, onRetryTrial, onReload }) => {
  const { t } = useTranslation();
  const { username, organizationId, isPublicAccess } = useUser();
  const { serverUrl } = useWebSocket();
  // Drafts are tab-local and isolated by account, server and organization.
  const draftStorage = useMemo(() => createNavigationStorage(serverUrl, {
    username, organizationId, isPublicAccess,
  }, window.sessionStorage), [serverUrl, username, organizationId, isPublicAccess]);
  const preparing = data?.state === 'provisioning';
  const [activationSlow, setActivationSlow] = useState(false);
  useEffect(() => {
    setActivationSlow(false);
    if (!preparing) return undefined;
    const timer = setTimeout(() => setActivationSlow(true), 20000);
    return () => clearTimeout(timer);
  }, [preparing, organizationId]);
  const ready = Boolean(data && String(data.organization_id) === String(organizationId)
    && ['trial', 'active', 'existing'].includes(data.state) && !trialBlocked && canCreate && !busy && !error);
  const notice = <>
    {!data && !error && <LinearProgress aria-label={t('saas.welcome_loading')} />}
    {error && <Alert severity="warning" action={<Button color="inherit" disabled={busy} onClick={preparing && data.role === 'owner' ? onRetryTrial : onReload}>{t(preparing && data.role === 'owner' ? 'saas.retry_trial' : 'saas.welcome_reload')}</Button>}>
      {t(preparing ? 'saas.welcome_trial_unavailable' : 'saas.welcome_unavailable')}
    </Alert>}
    {preparing && !error && <Alert severity="info" action={<Button color="inherit" disabled={busy} onClick={activationSlow && data.role === 'owner' ? onRetryTrial : onReload}>{t(activationSlow && data.role === 'owner' ? 'saas.retry_trial' : 'saas.welcome_reload')}</Button>}>{t(activationSlow ? 'first_result.activation_slow' : 'first_result.preparing_trial')}</Alert>}
    {trialBlocked && <Alert severity="warning" action={<Button component={OrganizationLink} color="inherit">{t('first_result.manage_access')}</Button>}>{t(expired ? 'saas.trial_expired' : 'saas.trial_exhausted')}</Alert>}
    {data && !ready && !preparing && !trialBlocked && !busy && !error && canCreate && <Alert severity="info" action={<Button component={OrganizationLink} color="inherit">{t('first_result.manage_access')}</Button>}>{t('saas.welcome_activation_required')}</Alert>}
  </>;

  return <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', backgroundImage: theme => `radial-gradient(ellipse at 92% 8%, ${alpha(theme.palette.primary.main, 0.09)}, transparent 42%), radial-gradient(ellipse at 6% 72%, ${alpha('#49b9ad', 0.055)}, transparent 40%)` }}>
    <Stack direction="row" spacing={2} sx={{ maxWidth: 980, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, pt: 3 }} alignItems="center" justifyContent="space-between">
      <Stack direction="row" spacing={1} alignItems="center">
        <Box component="img" src={dataunfoldMark} alt="" sx={{ width: 28, height: 28 }} />
        <Typography sx={{ fontSize: 17, fontWeight: 650, letterSpacing: '-0.035em' }}>DataUnfold</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '45%', overflowWrap: 'anywhere', textAlign: 'right' }}>{data?.name}</Typography>
    </Stack>
    {canCreate ? <AddProject key={JSON.stringify([serverUrl, username, organizationId])} onboardingMode onboardingCanStart={ready} onboardingNotice={notice} onboardingStorage={draftStorage} />
      : <Stack spacing={3} sx={{ p: 4, maxWidth: 820, mx: 'auto' }}>
        <Typography component="h1" variant="h4">{t('saas.welcome_title')}</Typography>
        <Typography>{t('saas.welcome_read_only')}</Typography>
        {notice}
        <Box><Button component={Link} to="/project-dashboard" variant="contained">{t('saas.view_projects')}</Button></Box>
      </Stack>}
  </Box>;
};

export default SaasWelcome;
